import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    alpha,
    Button,
} from "@mui/material";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Link as RouterLink } from "react-router-dom";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { fetchSchoolResourceOverview } from "../../../utils/termResourceApi";

function Kpi({ label, value, sub, color }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: (t) => `1px solid ${t.palette.divider}`,
                height: "100%",
            }}
        >
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" fontWeight={800} color={color}>
                {value}
            </Typography>
            {sub ? (
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            ) : null}
        </Paper>
    );
}

const TYPE_LABELS = {
    PastPaper: "Past papers",
    Notes: "Notes",
    Book: "Books",
    TLM: "TLMs",
};

export default function AdminTermResourcesOverview() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const overview = await fetchSchoolResourceOverview(schoolId);
            setData(overview);
            setMessage("");
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load resource overview.");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const periodLabel = data?.period
        ? [data.period.term, data.period.academicYear].filter(Boolean).join(" · ")
        : "Current term";

    if (loading) {
        return (
            <Stack alignItems="center" py={8}>
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            Resources & library
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Term distribution overview
                        </Typography>
                        <Chip size="small" label={periodLabel} color="primary" variant="outlined" sx={{ mt: 1 }} />
                    </Box>
                    <Button
                        component={RouterLink}
                        to="/Admin/library"
                        variant="outlined"
                        startIcon={<MenuBookOutlinedIcon />}
                    >
                        Library inventory
                    </Button>
                </Stack>

                {message ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Kpi
                            label="Digital resources"
                            value={data?.digital?.totalResources ?? 0}
                            sub="Uploaded this term"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Kpi
                            label="Total downloads"
                            value={data?.digital?.totalDownloads ?? 0}
                            sub="Usage tracking"
                            color="primary.main"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Kpi
                            label="Term borrowings"
                            value={data?.physical?.termBorrowings ?? 0}
                            sub="Physical books & TLMs"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Kpi
                            label="Overdue loans"
                            value={data?.physical?.overdueLoans ?? 0}
                            sub="Needs follow-up"
                            color={data?.physical?.overdueLoans ? "error.main" : undefined}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <FolderSharedOutlinedIcon color="primary" />
                                <Typography variant="h6" fontWeight={700}>
                                    Digital by type
                                </Typography>
                            </Stack>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {Object.entries(data?.digital?.byType || {}).map(([type, count]) => (
                                    <Chip key={type} label={`${TYPE_LABELS[type] || type}: ${count}`} variant="outlined" />
                                ))}
                                {!Object.keys(data?.digital?.byType || {}).length ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No digital resources this term.
                                    </Typography>
                                ) : null}
                            </Stack>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                By class
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {Object.entries(data?.digital?.byClass || {}).map(([cls, count]) => (
                                    <Chip key={cls} size="small" label={`${cls}: ${count}`} />
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <MenuBookOutlinedIcon color="secondary" />
                                <Typography variant="h6" fontWeight={700}>
                                    Physical library (term)
                                </Typography>
                            </Stack>
                            <Stack spacing={1}>
                                <Typography variant="body2">
                                    Active loans: <strong>{data?.physical?.activeLoans ?? 0}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Returned: <strong>{data?.physical?.returnedLoans ?? 0}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Inventory: <strong>{data?.physical?.availableCopies ?? 0}</strong> of{" "}
                                    {data?.physical?.inventoryCopies ?? 0} copies available
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <TrendingUpOutlinedIcon color="success" />
                                <Typography variant="h6" fontWeight={700}>
                                    Most downloaded this term
                                </Typography>
                            </Stack>
                            {(data?.digital?.topResources || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No download activity yet.
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Title</TableCell>
                                                <TableCell>Class</TableCell>
                                                <TableCell>Subject</TableCell>
                                                <TableCell>Teacher</TableCell>
                                                <TableCell align="right">Downloads</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(data?.digital?.topResources || []).map((row) => (
                                                <TableRow key={row.id || row._id}>
                                                    <TableCell>{row.title}</TableCell>
                                                    <TableCell>{row.class?.sclassName || "—"}</TableCell>
                                                    <TableCell>{row.subject?.subName || "—"}</TableCell>
                                                    <TableCell>{row.teacher?.name || "—"}</TableCell>
                                                    <TableCell align="right">{row.downloadCount ?? 0}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
